import React, { useEffect, useState } from "react"
import { DataEmissionPanel } from "./components/data-emission/data-emission-panel"
import { Box } from "@mui/system"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import { Grid, Typography, Paper } from "@mui/material"
import theme from "../../theme/theme"
import DatalakeHeader from "./components/Header"
import EmissionTabsWrappedLabel from "./components/emission-tab/emission-tab"
import { ContrapartesPanel } from "./components/emission-contrapartes/contrapartes-panel"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import GenericModal from "src/v2/components/modal/generic-modal"
import { BoxCreditEmissionButtonGroupAdmin } from "./components/box-admin-buttons-datalake-emission/box-admin-datalake-emission-admin-buttons"
import { EmissionSummarySections } from "./components/emission-sections/emission-summary-sections"
import EmissaoService from "src/services/emissao-service"
import { COLUMNS_MODAL_TABLE_CETIP_ISIN } from "./constants/full-list-cetip-isin.const"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import { useCetipModal } from "./hooks/use-cetip-isin-modal"
import CharacteristicsService from "src/services/characteristics-service"
import { TokenService } from "src/services/token-service"
import EmissionEmptyState from "./components/emission-empty-state/emission-empty-state"
import { BoxDataEventFlow } from "./components/box-data-event-flow/box-data-event-flow"
import { useParamaterCalcPrice } from "./hooks/use-parameter-calc-price"
import { useEventFincsTable } from "./hooks/use-event-fincs-table"
import EventosFluxoService from "src/services/eventos_fluxo_service"
import SelectComponent from "src/v2/components/select/select-component"
import Documents from "./sub-page/Documents"
import RegisterDocumentService from "src/services/register-document-service"
import PropTypes from "prop-types"

const SectionPaper = ({ children, sx }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                background: theme.palette.background.paper,
                borderRadius: 0,
                ...sx,
            }}
        >
            {children}
        </Paper>
    )
}

SectionPaper.propTypes = {
    children: PropTypes.node.isRequired,
    sx: PropTypes.object,
}

const DataLakeEmissionV2 = () => {
    const emissaoService = new EmissaoService()
    const caracteristicasService = new CharacteristicsService()
    const tokenService = new TokenService()
    const eventsFluxService = new EventosFluxoService()
    const documentService = new RegisterDocumentService()

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [characterData, setCharacterData] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingCharacters, setIsLoadingCharacters] = useState(false)
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)

    const [eventFlowSelectValue, setEventFlowSelectValue] = useState("")
    const [maxFlowEvent, setMaxFlowEvent] = useState(1)

    const loadCharacters = async (value) => {
        if (!value || value === "") return

        setIsLoadingCharacters(true)

        try {
            const request =
                await caracteristicasService.getCharacteristics(value)

            if (request?.body?.length > 0) {
                setCharacterData(request.body[0])
            } else {
                setCharacterData({})
            }
        } catch (e) {
            /**/
        } finally {
            setIsLoadingCharacters(false)
        }
    }

    const {
        state,
        handleOpenModal,
        handleRowClick,
        callBackOnChangeInputHeader,
        dispatch,
    } = useCetipModal(emissaoService)

    const { state: parameterCalcState, actions: parameterCalcActions } =
        useParamaterCalcPrice(caracteristicasService, state.selectCetipIsin)

    const { state: tableEventFluxState, actions: tableEventFluxActions } =
        useEventFincsTable(
            eventsFluxService,
            state.selectCetipIsin,
            eventFlowSelectValue
        )

    const rowFluxEvent =
        tableEventFluxState?.data?.data?.[`fluxoBase${eventFlowSelectValue}`]
            ?.data

    useEffect(() => {
        if (!state.selectCetipIsin) return

        const loadData = async () => {
            await parameterCalcActions.loadDataGetFincs()
            await tableEventFluxActions.loadData()
            await loadCharacters(state.selectCetipIsin)
        }

        loadData()
    }, [state.selectCetipIsin])

    useEffect(() => {
        if (!tableEventFluxState.data || !rowFluxEvent?.length) return

        const loadData = async () => {
            await parameterCalcActions.loadDataGetFincs(
                rowFluxEvent[0].data_evento
            )
        }

        loadData()
    }, [tableEventFluxState.data])

    useEffect(() => {
        if (!characterData?.FluxoBase) return

        setMaxFlowEvent(characterData.FluxoBase)
        setEventFlowSelectValue(characterData.FluxoBase)
    }, [characterData])

    useEffect(() => {
        const loadData = async () => {
            await parameterCalcActions.loadDataGetFincs(
                rowFluxEvent?.[0]?.data_evento
            )
        }

        loadData()
    }, [tableEventFluxState.data])

    useEffect(() => {
        const loadData = async () => {
            await parameterCalcActions.loadDataGetFincs()
            await tableEventFluxActions.loadData()

            const valueSearch = state.selectCetipIsin

            if (valueSearch !== "") {
                await loadCharacters(valueSearch)
            }
        }

        loadData()
    }, [state.selectCetipIsin])

    useEffect(() => {
        const lastFlowEvent = characterData?.FluxoBase

        setMaxFlowEvent(lastFlowEvent)
        setEventFlowSelectValue(lastFlowEvent)
    }, [characterData])

    useEffect(() => {
        const loadData = async () => {
            await tableEventFluxActions.loadData()
        }

        loadData()
    }, [eventFlowSelectValue])

    const pagination = { page: 0 }

    return (
        <SideBarLayout title="Emissão">
            {state.isLoadingModalData ||
                isLoadingCharacters ||
                parameterCalcState.anyLoading ||
                isLoadingDocuments ||
                isLoading ? (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            ) : (
                <GenericModal
                    isOpen={
                        state.listFullModalVisible && !state.isLoadingModalData
                    }
                    onClose={() => dispatch({ type: "TOGGLE_MODAL" })}
                    title="Lista de CETIP/ISIN"
                    width="65%"
                >
                    <DataGridTable
                        columns={COLUMNS_MODAL_TABLE_CETIP_ISIN}
                        rows={state.row}
                        pagination={pagination}
                        pageSize={25}
                        disableDeleteColumn={true}
                        onRowClick={handleRowClick}
                        pointerCursor={true}
                    />
                </GenericModal>
            )}

            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeader
                        emissaoService={emissaoService}
                        callBackOnChange={callBackOnChangeInputHeader}
                        handleClickFullList={handleOpenModal}
                        externalValue={state.selectCetipIsin}
                    />
                </Box>

                {!state.selectCetipIsin &&
                    Object.keys(characterData).length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <EmissionEmptyState
                            handleClickFullList={handleOpenModal}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: theme.palette.background.paper,
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <Box>
                            <EmissionTabsWrappedLabel
                                emissionTabChildren={
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            background: theme.palette.background.primary,
                                        }}
                                    >
                                        <SectionPaper>

                                            <DataEmissionPanel characterData={characterData} />
                                        </SectionPaper>

                                        <SectionPaper
                                            sx={{
                                                mt: 3,
                                            }}
                                        >
                                            <ContrapartesPanel characterData={characterData} />

                                            {isAdminUser && (
                                                <SectionPaper
                                                    sx={{
                                                        mt: 1,
                                                    }}
                                                >
                                                    <BoxCreditEmissionButtonGroupAdmin
                                                        cetipIsin={state.selectCetipIsin}
                                                        callBackSetIsLoading={(value) => {
                                                            setIsLoading(value)
                                                        }}
                                                    />
                                                </SectionPaper>
                                            )}

                                            <SectionPaper
                                                sx={{
                                                    mt: 1,
                                                }}
                                            >
                                                <EmissionSummarySections
                                                    cetipIsin={state.selectCetipIsin}
                                                    emissaoService={emissaoService}
                                                    characterData={characterData}
                                                    admin={isAdminUser}
                                                />
                                            </SectionPaper>
                                        </SectionPaper>




                                    </Box>
                                }
                                eventFlowTabChildren={
                                    <Box
                                        sx={{
                                            height: "100%",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 3,
                                            p: 3,
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <SectionPaper>
                                            <SelectComponent
                                                label="Fluxo"
                                                value={`Fluxo base ${eventFlowSelectValue}`}
                                                onChange={(e) =>
                                                    setEventFlowSelectValue(
                                                        e.target.value.match(
                                                            /\d+/
                                                        )?.[0] ?? ""
                                                    )
                                                }
                                                options={Array.from(
                                                    { length: maxFlowEvent },
                                                    (_, i) =>
                                                        `Fluxo base ${i + 1}`
                                                )}
                                            />

                                            <BoxDataEventFlow
                                                eventFlowData={
                                                    parameterCalcState?.data
                                                        ?.fincsEvent
                                                }
                                            />
                                        </SectionPaper>

                                        <SectionPaper>
                                            <Typography
                                                variant="h3"
                                                color="common.white"
                                            >
                                                Eventos do fluxo
                                            </Typography>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflowX: "auto",
                                                    width: "100%",
                                                    display: "block",
                                                    mt: 3,
                                                }}
                                            >
                                                <Box>
                                                    <DataGridTable
                                                        columns={
                                                            tableEventFluxState.columns
                                                        }
                                                        rows={
                                                            rowFluxEvent ?? []
                                                        }
                                                        pagination={pagination}
                                                        disableDeleteColumn
                                                        getRowId={(row) =>
                                                            row.uuid
                                                        }
                                                        loading={
                                                            tableEventFluxState.loading
                                                        }
                                                        resetKey={`fluxoBase${eventFlowSelectValue}`}
                                                    />
                                                </Box>
                                            </Box>
                                        </SectionPaper>
                                    </Box>
                                }
                                documentsChildren={
                                    <Box
                                        sx={{
                                            height: "100vh",
                                            width: {
                                                lg: "calc(100vw - 300px)",
                                            },
                                            p: 3,
                                        }}
                                    >
                                        <SectionPaper>
                                            <Documents
                                                isAdmin={isAdminUser}
                                                documentService={
                                                    documentService
                                                }
                                                cetip={state.selectCetipIsin}
                                                setIsLoading={
                                                    setIsLoadingDocuments
                                                }
                                            />
                                        </SectionPaper>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default DataLakeEmissionV2